// ABOUTME: Reusable template editor component for Make It Right tasks with drag-and-drop functionality
// ABOUTME: Used in child edit modals to customize the Make It Right task list

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface MakeItRightTemplateEditorProps {
  tasks: string[];
  onChange: (tasks: string[]) => void;
  onRestoreDefaults?: () => Promise<void>;
  showRestoreButton?: boolean;
}

interface SortableTaskItemProps {
  id: string;
  task: string;
  index: number;
  onUpdate: (index: number, value: string) => void;
  onDelete: (index: number) => void;
}

function SortableTaskItem({ id, task, index, onUpdate, onDelete }: SortableTaskItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
      }}
    >
      <IconButton size="small" {...attributes} {...listeners} aria-label={`Reorder task ${index + 1}`}>
        <DragIndicatorIcon />
      </IconButton>
      <TextField
        fullWidth
        value={task}
        onChange={(e) => onUpdate(index, e.target.value)}
        placeholder={`Task ${index + 1}`}
        error={!task.trim()}
        helperText={!task.trim() ? 'Task cannot be empty' : ''}
      />
      <IconButton onClick={() => onDelete(index)} color="error" aria-label={`Delete task ${index + 1}`}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
}

export default function MakeItRightTemplateEditor({
  tasks,
  onChange,
  onRestoreDefaults,
  showRestoreButton = true,
}: MakeItRightTemplateEditorProps) {
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tasks.findIndex((_, i) => `task-${i}` === active.id);
    const newIndex = tasks.findIndex((_, i) => `task-${i}` === over.id);

    onChange(arrayMove(tasks, oldIndex, newIndex));
  };

  const handleAddTask = () => {
    onChange([...tasks, '']);
  };

  const handleUpdateTask = (index: number, value: string) => {
    const updated = [...tasks];
    updated[index] = value;
    onChange(updated);
    setError(null);
  };

  const handleDeleteTask = (index: number) => {
    if (tasks.length <= 1) {
      setError('At least one task is required');
      return;
    }
    const updated = tasks.filter((_, i) => i !== index);
    onChange(updated);
    setError(null);
  };

  const handleRestoreDefaults = async () => {
    if (onRestoreDefaults) {
      try {
        await onRestoreDefaults();
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to restore defaults');
      }
    }
  };

  const hasEmptyTasks = tasks.some((t) => !t.trim());

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tasks.map((_, i) => `task-${i}`)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.map((task, index) => (
            <SortableTaskItem
              key={`task-${index}`}
              id={`task-${index}`}
              task={task}
              index={index}
              onUpdate={handleUpdateTask}
              onDelete={handleDeleteTask}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddTask}
          aria-label="Add new task"
        >
          Add Task
        </Button>

        {showRestoreButton && onRestoreDefaults && (
          <Button
            variant="outlined"
            onClick={handleRestoreDefaults}
            aria-label="Restore default tasks"
          >
            Restore Defaults
          </Button>
        )}
      </Box>

      {hasEmptyTasks && (
        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
          Please fill in all tasks before saving
        </Typography>
      )}
    </Box>
  );
}
